import { ActionButtonProps, ButtonTT } from '@components-common/buttons';
import { button_defaults } from './defaults';
import { ConfirmationActionButton } from '../../../components-common/modals/ConfirmationButton';
import { ConfirmationModalProps } from '../../../components-common/modals';
import { NavigationButton } from '../../../components-common/buttons/NavigationButton';
import { ActionButtonConfig, RowData } from './types';
import { findPropInTree } from './process-button';
import {
  cloneTableRowByEndpoint,
  deleteTableRow,
} from '../../../components-common/_shared/actionButton.helper';

const findId = (obj: any): string => findPropInTree('id', obj);

const view: Partial<Partial<ActionButtonConfig>> = {
  ...button_defaults.view,
  actionToDispatch: {
    action: 'navigate_to',
    payload: {
      relative: true,
      originalAction: 'view',
    },
  },
};

// Copy button configuration with destructured properties and commented mapper
const copy: Partial<ActionButtonConfig> = {
  ...button_defaults.copy,
  actionToDispatch: {
    action: 'copy',
    payload: {
      relative: true,
      originalAction: 'copy',
    },
  },
};

// Edit button configuration with direct mapper definition
const edit: Partial<ActionButtonConfig> = {
  ...button_defaults.edit,
  Component: NavigationButton,
  actionToDispatch: {
    action: 'navigate_to',
    payload: {
      relative: true,
      originalAction: 'edit',
    },
  },
  mapper: (data: any) => ({
    action: 'navigate_to',
    payload: {
      relative: true,
      originalAction: 'edit',
      path: findId(data),
    },
  }),
};

// Delete button configuration with complex confirm logic and mapper
const del: Partial<ActionButtonProps & any> = {
  ...button_defaults.delete,
  Component: ConfirmationActionButton,
  confirm: true,
  confirmProps: {
    title: 'Are you sure you want to proceed?',
    body: 'The current operation cannot be undone. Please confirm in order to continue.',
    actions: [
      {
        Component: ButtonTT,
        action: 'cancel',
        id: 'cancel',
        kind: 'cancel',
      },
      {
        Component: ButtonTT,
        kind: 'delete',
        id: 'delete',
        action: 'delete',
        label: 'Confirm',
        onClick: ({ onClickProps, actionToDispatch, onClose }: any) => {
          const { payload } = actionToDispatch;
          deleteTableRow({
            endpoint: payload.endpointToDelete,
            props: onClickProps,
            onClose: onClose,
          });
        },
      },
    ],
  },
  actionToDispatch: {
    action: 'delete',
    payload: {
      originalAction: 'delete',
    },
  },
};

// Open modal button configuration with complex confirmation logic
const openModal: Partial<
  ActionButtonConfig & {
    confirm?: boolean;
    confirmProps: ConfirmationModalProps;
  }
> = {
  ...button_defaults.open_modal,
  confirm: true,
  confirmProps: {
    title: 'Add users',
    body: 'kjbg',
    actions: [
      {
        Component: ButtonTT,
        action: 'cancel',
        id: 'cancel',
        kind: 'cancel',
        mapper: (rowData: RowData) => ({
          id: rowData.row.id,
          action: 'cancel',
        }),
      },
      {
        Component: ButtonTT,
        kind: 'delete',
        id: 'delete',
        action: 'delete',
        mapper: (rowData: RowData) => ({
          id: rowData.row.id,
          action: 'delete',
        }),
      },
    ],
  } as any,
  actionToDispatch: {
    action: 'delete',
    payload: {
      originalAction: 'open_modal',
    },
  },
  id: 'open_modal',
};

// Create new button configuration
const createNew: Partial<ActionButtonConfig> = {
  ...button_defaults.create_new,
  actionToDispatch: {
    action: 'create_new',
    payload: {
      originalAction: 'create_new',
      path: 'new',
      relative: true,
    },
  },
  text: 'Create',
};

// Back button configuration
const back: Partial<ActionButtonConfig> = {
  ...button_defaults.back,
  actionToDispatch: {
    action: 'navigate_back',
    payload: {
      originalAction: 'back',
      path: -1,
      relative: true,
    },
  },
  // mapper: (s: any) => (),
};

// Save button configuration
const save: Partial<ActionButtonConfig> = {
  ...button_defaults.save,
  actionToDispatch: {
    action: 'save',
  },
  //mapper: (s: any) => ()
};

// Save and create new button configuration
const saveCreate: Partial<ActionButtonConfig> = {
  ...button_defaults.save_create,
  actionToDispatch: {
    action: 'save_create',
    payload: {
      originalAction: 'save_create',
    },
  },
  //mapper: (s: any) => ()
};

// Delete button configuration with complex confirm logic and mapper
const cop: Partial<ActionButtonProps & any> = {
  ...button_defaults.copy,
  Component: ConfirmationActionButton,
  confirm: true,
  confirmProps: {
    title: 'Are you sure you want to clone?',
    body: 'The current operation cannot be undone. Please confirm in order to continue.',
    actions: [
      {
        Component: ButtonTT,
        action: 'cancel',
        id: 'cancel',
        kind: 'cancel',
      },
      {
        Component: ButtonTT,
        kind: 'copy',
        id: 'copy',
        action: 'copy',
        label: 'Confirm',
        onClick: ({ onClickProps, actionToDispatch, onClose }: any) => {
          const { payload } = actionToDispatch;
          cloneTableRowByEndpoint({
            endpoint: payload.copy,
            props: onClickProps,
            onClose: onClose,
          });
        },
      },
    ],
  },
  actionToDispatch: {
    action: 'copy',
    payload: {
      originalAction: 'copy',
    },
  },
};

// Consolidate all button configurations into a single defaults object
export const action_buttons_defaults = {
  view,
  copy: cop,
  edit,
  delete: del,
  open_modal: openModal,
  create_new: createNew,
  back,
  save,
  save_create: saveCreate,
};
